/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import {
  retrieveNotificationsSettings,
  saveNotificationsSettings,
} from "../../services/user";
import {
  IonPage,
  IonText,
  IonIcon,
  useIonRouter,
  IonContent,
} from "@ionic/react";
import { chevronForward } from "ionicons/icons";
import Header from "../../components/Header";
import BiWazeGoalModal from "../../components/ui/popups/BiwazeGoals";

// Define a type for notification items
interface NotificationItemType {
  title: string;
  subtitle: string;
}

interface TherapySettings {
  missedDays: {
    count: number;
    enabled: boolean;
  };
  pressureThresholds: {
    value: number;
    enabled: boolean;
  };
}

const NotificationPreferences: React.FC = () => {
  const router = useIonRouter();
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingItem, setEditingItem] = useState<NotificationItemType | null>(
    null
  );
  const [currentModalType, setCurrentModalType] = useState<string | null>(null);
  const [followName, setFollowName] = useState<string>(""); // Default value
  const [missedDaysGoal, setMissedDaysGoal] = useState<any>(null);
  const [pressureThresholdGoal, setPressureThresholdGoal] = useState<any>(null);
  const userIdFromParams = Number(
    router.routeInfo.pathname.split("/").filter(Boolean).pop()
  );
  const params = new URLSearchParams(router.routeInfo.search || "");
  const userName = params.get("user");
  useEffect(() => {
    if (userName) {
      setFollowName(
        userName.length > 10 ? userName.slice(0, 10) + "..." : userName
      );
    }
  }, []);

  const [therapySettings, setTherapySettings] = useState<TherapySettings>({
    missedDays: {
      count: 3,
      enabled: true,
    },
    pressureThresholds: {
      value: 10,
      enabled: true,
    },
  });
  const hasFetched = useRef(false);
  // Fetch notification data from backend
  useEffect(() => {
    if (hasFetched.current) return; // Prevent multiple fetches
    const fetchNotificationData = async () => {
      try {
        const response = await retrieveNotificationsSettings(userIdFromParams);

        // Update followName in state instead of local variable
        let name = "";
        // ⚠️ Same trap as MyFollowing: `|| ""` guarded the two FIELDS, so a missing profile made
        // both names empty and sent control straight into the bare `response.data.profile.phone`
        // below. Fourteen lines further down this same function already writes
        // `response?.data || response` — the object was known to be optional, just not here.
        const notifyProfile = response?.data?.profile;
        const firstName = notifyProfile?.first_name || "";
        const lastName = notifyProfile?.last_name || "";
        if (firstName && lastName) {
          name = `${firstName} ${lastName}`;
        } else if (notifyProfile?.phone) {
          name = notifyProfile.phone;
        } else if (notifyProfile?.email) {
          name = notifyProfile.email;
        } else {
          name = "Unknown User"; // Fallback if no name is available
        }

        // Set the name to state so it triggers re-render
        // You can now use `followName` as needed, e.g., set to state if required
        const goalData = response?.data || response;

        if (goalData) {
          // Missed Days Goal Data
          const missedDaysGoalData = {
            target_count: goalData.missed_days_count || 3,
            target_seconds: 0,
            target_hours: 0,
            isActive: goalData.track_missed_days ?? true,
          };

          // Pressure Threshold Goal Data
          const pressureThresholdGoalData = {
            target_count: goalData.pressure_threshold || 10,
            target_seconds: 0,
            target_hours: 0,
            isActive: goalData.track_pressure_alert ?? true,
          };

          setMissedDaysGoal(missedDaysGoalData);
          setPressureThresholdGoal(pressureThresholdGoalData);

          // Update therapy settings from fetched data
          setTherapySettings({
            missedDays: {
              count: goalData.missed_days_count || 3,
              enabled: goalData.track_missed_days ?? true,
            },
            pressureThresholds: {
              value: goalData.pressure_threshold || 10,
              enabled: goalData.track_pressure_alert ?? true,
            },
          });
        }
      } catch (error) {
        console.error("Failed to fetch notification data:", error);
        // Fallback goal data
        setMissedDaysGoal({
          target_count: 3,
          target_seconds: 0,
          target_hours: 0,
          isActive: true,
        });
        setPressureThresholdGoal({
          target_count: 10,
          target_seconds: 0,
          target_hours: 0,
          isActive: true,
        });
      }
    };

    fetchNotificationData();
  }, [userIdFromParams]); // Add dependency

  // Save settings
  const saveSettings = async () => {
    const data = {
      set_for: userIdFromParams, // Use global userId
      set_by_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      pressure_threshold: therapySettings.pressureThresholds.value,
      missed_days_count: therapySettings.missedDays.count,
      track_pressure_alert: therapySettings.pressureThresholds.enabled,
      track_missed_days: therapySettings.missedDays.enabled,
    };

    try {
      console.log("Saving settings:", data);
      await saveNotificationsSettings(data);
      console.log("Settings saved successfully");
    } catch (error) {
      console.error("Error saving notification settings:", error);
    }
  };

  // Handle back button
  const handleBack = () => {
    router.push("/settings/notifications");
  };

  const handleChange = (field: string, goalObject: any) => {
    console.log("HandleChange called with:", field, goalObject);

    const isActive = Object.prototype.hasOwnProperty.call(
      goalObject,
      "isActive"
    )
      ? goalObject.isActive
      : true;

    // Update the appropriate goal state based on current modal type and field
    if (currentModalType === "missedDays" && field === "target_count") {
      setMissedDaysGoal((prev: any) => ({
        ...prev,
        target_count: goalObject.target_count ?? goalObject,
        isActive: isActive,
      }));

      setTherapySettings((prev) => ({
        ...prev,
        missedDays: {
          ...prev.missedDays,
          count: goalObject.target_count ?? goalObject,
          enabled: isActive,
        },
      }));
    } else if (
      currentModalType === "pressureThresholds" &&
      field === "pressure_threshold"
    ) {
      setPressureThresholdGoal((prev: any) => ({
        ...prev,
        target_count: goalObject.target_count ?? goalObject,
        isActive: isActive,
      }));

      setTherapySettings((prev) => ({
        ...prev,
        pressureThresholds: {
          ...prev.pressureThresholds,
          value: goalObject.target_count ?? goalObject,
          enabled: isActive,
        },
      }));
    }
  };

  // Close modals
  const handleCloseModal = () => {
    setShowGoalModal(false);
    setCurrentModalType(null);
    setEditingItem(null);
  };

  const openModal = (section: string, item: NotificationItemType) => {
    setEditingItem(item);

    if (item.title === "Missed Days") {
      setCurrentModalType("missedDays");
      setShowGoalModal(true);
    } else if (item.title === "Pressure Thresholds") {
      setCurrentModalType("pressureThresholds");
      setShowGoalModal(true);
    }
  };

  // Handle save from the goal modal
  const handleGoalSave = async (updatedGoal: any) => {
    console.log("Updated Goal:", updatedGoal);

    if (currentModalType === "missedDays") {
      const newCount =
        updatedGoal.target_count ?? therapySettings.missedDays.count;
      const newEnabled =
        updatedGoal.isActive ?? therapySettings.missedDays.enabled;

      setTherapySettings((prev) => ({
        ...prev,
        missedDays: {
          count: newCount,
          enabled: newEnabled,
        },
      }));

      setMissedDaysGoal((prev: any) => ({
        ...prev,
        target_count: newCount,
        isActive: newEnabled,
      }));
    } else if (currentModalType === "pressureThresholds") {
      const newValue =
        updatedGoal.target_count ?? therapySettings.pressureThresholds.value;
      const newEnabled =
        updatedGoal.isActive ?? therapySettings.pressureThresholds.enabled;

      setTherapySettings((prev) => ({
        ...prev,
        pressureThresholds: {
          value: newValue,
          enabled: newEnabled,
        },
      }));

      setPressureThresholdGoal((prev: any) => ({
        ...prev,
        target_count: newValue,
        isActive: newEnabled,
      }));
    }

    // Save settings after updating state
    setTimeout(async () => {
      await saveSettings();
    }, 100);

    handleCloseModal();
  };

  const renderNotificationItem = (
    title: string,
    subtitle: string,
    section: string
  ) => (
    <div
      className="flex justify-between items-center p-4 border-b border-gray-200 last:border-b-0 cursor-pointer"
      onClick={() => openModal(section, { title, subtitle })}
    >
      <div>
        <h3 className="text-lg font-normal text-black mb-1">{title}</h3>
        <p className="text-gray-500">{subtitle}</p>
      </div>
      <button className="flex items-center justify-center">
        <IonIcon icon={chevronForward} style={{ fontSize: "24px" }} />
      </button>
    </div>
  );

  const renderNotificationSection = (
    title: string,
    items: NotificationItemType[]
  ) => (
    <>
      <div className="section-header mb-4">
        <IonText>
          <h2 className="text-gray-500 font-medium text-xs font-normal ml-2.5">
            {title}
          </h2>
        </IonText>
      </div>
      <div className="bg-white rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-6">
        {items.map((item, index) => (
          <div key={index}>
            {renderNotificationItem(item.title, item.subtitle, title)}
          </div>
        ))}
      </div>
    </>
  );

  // Render the BiWazeGoalModal for items that work with it
  const renderGoalModal = () => {
    if (!showGoalModal) return null;

    if (currentModalType === "missedDays" && missedDaysGoal) {
      return (
        <BiWazeGoalModal
          autoPresentOnMount={true}
          onClose={handleCloseModal}
          deviceName="Missed Days"
          showNumbers={true}
          showMinutes={false}
          toggleTitle="Send notification"
          subTitle={`Notify me when ${followName.split(" ")[0]} missed`}
          noOfTimes="Days"
          position="middle"
          goalData={{
            target_count: missedDaysGoal.target_count || 3,
            target_seconds: 0,
            target_hours: 0,
            isActive: missedDaysGoal.isActive ?? true,
          }}
          subText="of BiWaze therapies"
          showRemove={false}
          onChange={(value) => {
            console.log("Missed Days Count Changed:", value);
            handleChange("target_count", value);
          }}
          onSave={handleGoalSave}
        />
      );
    }

    if (currentModalType === "pressureThresholds" && pressureThresholdGoal) {
      return (
        <BiWazeGoalModal
          autoPresentOnMount={true}
          onClose={handleCloseModal}
          deviceName="Pressure Thresholds"
          showNumbers={false}
          toggleTitle="Send notification"
          subTitle="Notify me when my pressures are"
          noOfTimes={
            <>
              cmH<sub>2</sub>O
            </>
          }
          position="middle"
          goalData={{
            target_count: pressureThresholdGoal.target_count || 10,
            target_seconds: 0,
            target_hours: 0,
            isActive: pressureThresholdGoal.isActive ?? true,
          }}
          subText="off from set pressures"
          showRemove={false}
          showCounter={true}
          onChange={(value) => {
            console.log("Pressure Threshold Count Changed:", value);
            handleChange("pressure_threshold", value);
          }}
          onSave={handleGoalSave}
        />
      );
    }

    return null;
  };

  const therapyNotifications: NotificationItemType[] = [
    {
      title: "Missed Days",
      subtitle: therapySettings.missedDays.enabled
        ? `${therapySettings.missedDays.count} day${
            therapySettings.missedDays.count !== 1 ? "s in a row" : ""
          } `
        : "Off",
    },
    {
      title: "Pressure Thresholds",
      subtitle: therapySettings.pressureThresholds.enabled
        ? `± ${String(therapySettings.pressureThresholds.value)} cmH₂O`
        : "Off",
    },
  ];

  return (
    <IonPage>
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle={followName}
        showBackButton={true}
        onBackButtonClick={handleBack}
      />
      <IonContent className="ion-padding" style={{ "--background": "#F7F5FA" }}>
        <div className="flex flex-col">
          {renderNotificationSection("SET THERAPY NOTIFICATIONS", [
            {
              title: "Missed Days",
              subtitle: therapySettings.missedDays.enabled
                ? `${therapySettings.missedDays.count} day${
                    therapySettings.missedDays.count !== 1 ? "s in a row" : ""
                  } `
                : "Off",
            },
            // {
            //   title: "Pressure Thresholds",
            //   subtitle: therapySettings.pressureThresholds.enabled
            //     ? `± ${String(therapySettings.pressureThresholds.value)} cmH₂O`
            //     : "Off",
            // },
          ])}
        </div>

        {/* Render the appropriate modal */}
        {renderGoalModal()}
      </IonContent>
    </IonPage>
  );
};

export default NotificationPreferences;
